import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config";

const ARES_API = "https://ares.gov.cz/ekonomicke-subjekty-v-be/rest/ekonomicke-subjekty/";

export default function LeadtoolRegister() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    ico: "",
    companyName: "",
    address: "",
    dic: "",
    vatPayer: false,
    email: "",
    password: "",
    passwordConfirm: "",
    bankAccount: "",
    agreedToTerms: false
  });
  const [loading, setLoading] = useState(false);
  const [aresLoading, setAresLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const lookupAres = async () => {
    const ico = form.ico.trim();
    if (!ico || ico.length < 6) {
      setError("Zadejte platné IČO (6–8 číslic)");
      return;
    }
    setAresLoading(true);
    setError("");
    try {
      const res = await fetch(`${ARES_API}${ico}`);
      if (!res.ok) throw new Error("IČO nebylo nalezeno v registru ARES");
      const data = await res.json();
      const name = data.obchodniJmeno || data.nazev || "";
      const sidlo = data.sidlo || {};
      const address = [
        sidlo.nazevUlice,
        sidlo.cisloDomovni ? `${sidlo.cisloDomovni}${sidlo.cisloOrientacni ? "/" + sidlo.cisloOrientacni : ""}` : "",
        sidlo.nazevObce,
        sidlo.psc ? String(sidlo.psc).replace(/(\d{3})(\d{2})/, "$1 $2") : ""
      ].filter(Boolean).join(", ");
      // DIČ a DPH z ARES
      const dic = data.dic || "";
      const vatPayer = !!dic; // pokud má DIČ, je plátce DPH
      setForm(f => ({ ...f, companyName: name, address, dic, vatPayer }));
    } catch (e) {
      setError(e.message || "Chyba při vyhledávání v ARES");
    } finally {
      setAresLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.passwordConfirm) {
      setError("Hesla se neshodují");
      return;
    }
    if (form.password.length < 6) {
      setError("Heslo musí mít alespoň 6 znaků");
      return;
    }
    if (!form.agreedToTerms) {
      setError("Musíte souhlasit s podmínkami");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/supplier/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: form.email,
          password: form.password,
          ico: form.ico,
          dic: form.dic,
          vatPayer: form.vatPayer,
          companyName: form.companyName,
          address: form.address,
          bankAccount: form.bankAccount,
          agreedToTerms: form.agreedToTerms,
          agreedToTermsVersion: "1.1"
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Chyba při registraci");
      setSuccess(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Registrace úspěšná!</h2>
          <p className="text-gray-600 mb-6">Váš účet byl vytvořen a čeká na schválení administrátorem. Po schválení vám zašleme informaci emailem.</p>
          <button onClick={() => navigate("/leadtool")} className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-xl transition-colors">
            Přejít na přihlášení
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <img src="/vyjedname-logo.png" alt="Vyjednáme" className="h-9 object-contain" />
            <div className="h-8 w-px bg-gray-300" />
            <img src="/vodafone-partner-business.png" alt="Vodafone Business Partner" className="h-9 object-contain" />
          </div>
          <button onClick={() => navigate("/leadtool")} className="text-sm text-red-600 hover:text-red-700 font-medium">
            Přihlásit se →
          </button>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-red-600 to-red-700 px-8 py-6">
            <h1 className="text-2xl font-bold text-white">Registrace dodavatele</h1>
            <p className="text-red-100 mt-1 text-sm">Registrace do leadového partnerského programu</p>
          </div>

          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">{error}</div>
            )}

            {/* IČO sekce */}
            <div>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Identifikace firmy</h2>
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">IČO *</label>
                  <input
                    type="text"
                    name="ico"
                    value={form.ico}
                    onChange={handleChange}
                    placeholder="12345678"
                    maxLength={8}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    required
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={lookupAres}
                    disabled={aresLoading}
                    className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-xl transition-colors disabled:opacity-50 whitespace-nowrap"
                  >
                    {aresLoading ? "Hledám..." : "Doplnit z ARES"}
                  </button>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Název firmy *</label>
              <input
                type="text"
                name="companyName"
                value={form.companyName}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Adresa sídla</label>
              <input
                type="text"
                name="address"
                value={form.address}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">DIČ</label>
              <input
                type="text"
                name="dic"
                value={form.dic}
                onChange={handleChange}
                placeholder="CZ12345678"
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-400 mt-1">Vyplní se automaticky z ARES, pokud jste registrovaný plátce DPH.</p>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="vatPayer"
                name="vatPayer"
                checked={form.vatPayer}
                onChange={handleChange}
                className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
              />
              <label htmlFor="vatPayer" className="text-sm font-medium text-gray-700 cursor-pointer">
                Jsem plátce DPH
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Číslo bankovního účtu</label>
              <input
                type="text"
                name="bankAccount"
                value={form.bankAccount}
                onChange={handleChange}
                placeholder="123456789/0800"
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-400 mt-1">Zadejte ve formátu číslo účtu/kód banky (např. 123456789/0800).</p>
            </div>

            {/* Přihlašovací údaje */}
            <div className="border-t pt-6">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Přihlašovací údaje</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Heslo * (min. 6 znaků)</label>
                    <input
                      type="password"
                      name="password"
                      value={form.password}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Potvrdit heslo *</label>
                    <input
                      type="password"
                      name="passwordConfirm"
                      value={form.passwordConfirm}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Podmínky */}
            <div className="border-t pt-6">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Smluvní podmínky partnerského programu</h2>
              <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-600 space-y-3 mb-4 max-h-72 overflow-y-auto border border-gray-200">
                <p className="font-bold text-gray-800 text-base">Podmínky partnerské spolupráce – Leadový program</p>
                <p className="text-xs text-gray-500">Provozovatel: Vyjednáme, s.r.o., IČO 21377596, se sídlem Příčná 1892/4, Nové Město, 110 00 Praha 1 (dále jen „Provozovatel")</p>

                <p className="font-semibold text-gray-800 mt-2">1. Definice pojmů</p>
                <p><strong>Lead</strong> – kontakt na potenciálního zákazníka se zájmem o telekomunikační služby Vodafone, předaný Dodavatelem prostřednictvím tohoto portálu.</p>
                <p><strong>FC (Forecast)</strong> – procentuální ukazatel uzavření obchodního případu; hodnota FC = 100 % označuje úspěšně uzavřený obchodní případ s podepsanou smlouvou.</p>
                <p><strong>BMSL (Base Monthly Service Level)</strong> – minimální měsíční závazek zákazníka v Kč bez DPH, sloužící jako základ pro výpočet odměny.</p>

                <p className="font-semibold text-gray-800 mt-2">2. Předmět spolupráce</p>
                <p>Dodavatel se zavazuje předávat Provozovateli leady na potenciální firemní zákazníky se zájmem o telekomunikační produkty a služby Vodafone. Provozovatel se zavazuje tyto leady zpracovat, zákazníky kontaktovat a v případě úspěšného uzavření smlouvy vyplatit Dodavateli smluvní odměnu dle čl. 3.</p>

                <p className="font-semibold text-gray-800 mt-2">3. Odměna a způsob výpočtu</p>
                <p>3.1 Za každý úspěšně uzavřený obchodní případ (FC = 100) náleží Dodavateli odměna ve výši <strong>120 % hodnoty BMSL</strong> příslušného zákazníka za první fakturační měsíc.</p>
                <p>3.2 Odměna je nenároková v případě, že zákazník odstoupí od smlouvy do 30 dnů od jejího uzavření nebo pokud lead nesplňuje kvalitativní kritéria stanovená Provozovatelem v bodě 3.3.</p>
                <p>3.3 Provozovatel si vyhrazuje právo odmítnout lead, který nesplňuje minimální kritéria kvality (duplicita, neplatný kontakt, zákazník mimo cílovou skupinu). O důvodu zamítnutí leadu bude Dodavatel informován prostřednictvím portálu.</p>

                <p className="font-semibold text-gray-800 mt-2">4. Fakturace a splatnost</p>
                <p>4.1 Odměna bude Dodavateli vyplacena na základě jím vystavené faktury (daňového dokladu). Podkladem pro vystavení faktury je měsíční report úspěšně uzavřených obchodních případů, který Provozovatel zpřístupní Dodavateli v portálu (nebo zašle e-mailem) nejpozději do 15. dne měsíce následujícího po hodnoceném období.</p>
                <p>4.2 Odměny jsou splatné do <strong>60 dnů</strong> od data doručení bezchybné faktury Provozovateli. Platba probíhá převodem na bankovní účet uvedený při registraci.</p>
                <p>4.3 Dodavatel je povinen neprodleně oznámit změnu bankovního účtu. Za škody vzniklé pozdním oznámením odpovídá Dodavatel.</p>
                <p>4.4 Dodavatel prohlašuje, že je nebo není plátcem DPH dle zákona č. 235/2004 Sb., a je povinen tuto skutečnost při každé změně aktualizovat. Provozovatel je oprávněn stav plátcovství a případný status nespolehlivého plátce nezávisle ověřovat ve veřejných registrech.</p>

                <p className="font-semibold text-gray-800 mt-2">5. Práva a povinnosti dodavatele</p>
                <p>5.1 Dodavatel se zavazuje předávat pouze leady, ke kterým má zákonný souhlas kontaktované osoby se zpracováním osobních údajů a obchodním oslovením. Dodavatel je povinen na výzvu Provozovatele nejpozději do 3 pracovních dnů doložit prokazatelný souhlas konkrétního leadu s předáním údajů a obchodním oslovením.</p>
                <p>5.2 Dodavatel nesmí poskytovat nepravdivé, zavádějící nebo duplicitní leady.</p>
                <p>5.3 Dodavatel nesmí jednat v rozporu se zájmy Provozovatele ani kontaktovat zákazníky jménem Vodafone bez písemného souhlasu.</p>
                <p>5.4 Dodavatel se zavazuje udržovat přihlašovací údaje k portálu v tajnosti a neprodleně oznámit podezření na jejich zneužití.</p>

                <p className="font-semibold text-gray-800 mt-2">6. Ochrana osobních údajů (GDPR)</p>
                <p>6.1 Zpracování osobních údajů probíhá v souladu s nařízením EU 2016/679 (GDPR) a zákonem č. 110/2019 Sb.</p>
                <p>6.2 Provozovatel zpracovává údaje Dodavatele (název firmy, IČO, e-mail, adresa, bankovní spojení) za účelem plnění smluvního vztahu, vedení účetnictví a komunikace.</p>
                <p>6.3 Dodavatel jako předávající osobních údajů zákazníků (leadů) vystupuje v roli samostatného správce a odpovídá za zákonnost jejich předání Provozovateli. V případě porušení této povinnosti nese Dodavatel plnou odpovědnost za případné sankce ze strany dozorových orgánů.</p>
                <p>6.4 Osobní údaje zákazníků předané v leadech budou zpracovávány výhradně za účelem obchodního oslovení v rámci partnerského programu a nebudou předávány třetím stranám bez zákonného důvodu.</p>
                <p>6.5 Dodavatel má právo na přístup ke svým osobním údajům, jejich opravu, výmaz, omezení zpracování a přenositelnost dle čl. 15–20 GDPR. Žádosti zasílejte na: info@vyjedname.cz.</p>

                <p className="font-semibold text-gray-800 mt-2">7. Mlčenlivost a důvěrnost</p>
                <p>7.1 Obě strany se zavazují zachovávat mlčenlivost o obchodních podmínkách, odměnách, zákaznických datech a interních procesech Provozovatele.</p>
                <p>7.2 Povinnost mlčenlivosti trvá i po ukončení spolupráce, a to po dobu 5 let.</p>

                <p className="font-semibold text-gray-800 mt-2">8. Trvání a ukončení spolupráce</p>
                <p>8.1 Spolupráce vzniká schválením registrace Provozovatelem a trvá na dobu neurčitou.</p>
                <p>8.2 Každá ze stran může spolupráci ukončit písemnou výpovědí s výpovědní dobou 30 dnů.</p>
                <p>8.3 Provozovatel je oprávněn spolupráci ukončit s okamžitou účinností při porušení těchto podmínek Dodavatelem.</p>
                <p>8.4 Ukončením spolupráce nezaniká nárok na odměny za leady předané před datem ukončení, u kterých dojde k úspěšnému uzavření obchodu (FC = 100 %) nejpozději do 3 měsíců od uplynutí výpovědní doby nebo okamžitého ukončení.</p>

                <p className="font-semibold text-gray-800 mt-2">9. Odpovědnost a záruky</p>
                <p>9.1 Provozovatel negarantuje minimální počet uzavřených obchodních případů ani minimální výši odměn.</p>
                <p>9.2 Provozovatel neodpovídá za škody vzniklé výpadkem portálu z důvodů vyšší moci nebo plánované údržby.</p>
                <p>9.3 Dodavatel odpovídá za škody způsobené předáním nekvalitních, duplicitních nebo nepravdivých leadů.</p>

                <p className="font-semibold text-gray-800 mt-2">10. Závěrečná ustanovení a souhlas</p>
                <p>10.1 Tyto podmínky se řídí právním řádem České republiky, zejm. zákonem č. 89/2012 Sb. (občanský zákoník).</p>
                <p>10.2 Případné spory budou řešeny přednostně smírnou cestou, jinak u věcně a místně příslušného soudu v České republice.</p>
                <p>10.3 Dodavatel výslovně souhlasí s tím, že závazná smlouva, jejíž nedílnou součástí jsou tyto podmínky, se uzavírá distančním způsobem v elektronické podobě, a to zaškrtnutím příslušného souhlasu při registraci do portálu. Za účelem právní jistoty a prokazatelnosti uzavření smlouvy obě strany berou na vědomí a souhlasí s tím, že systém Provozovatele neprůstřelně loguje a bezpečně ukládá do databáze následující elektronickou stopu o udělení souhlasu:</p>
                <ul className="list-disc list-inside ml-2 space-y-1">
                  <li>Identifikační údaje (uživatelský účet, e-mail, zadané IČO),</li>
                  <li>Přesný čas udělení souhlasu (časové razítko s přesností na sekundy),</li>
                  <li>IP adresu, ze které byl souhlas udělen,</li>
                  <li>Konkrétní verzi odsouhlasených podmínek.</li>
                </ul>
                <p>10.4 Provozovatel je oprávněn tyto podmínky v přiměřeném rozsahu jednostranně změnit. Změna bude Dodavateli oznámena e-mailem nejméně 14 dní předem. Dodavatel má právo změnu odmítnout a z tohoto důvodu závazek vypovědět s výpovědní dobou 14 dnů. Pokračování v užívání portálu po uplynutí lhůty pro oznámení změny bez podání výpovědi znamená souhlas se změnou.</p>
                <p>10.5 Je-li některé ustanovení těchto podmínek neplatné nebo nevymahatelné, ostatní ustanovení zůstávají v platnosti.</p>
                <p className="text-xs text-gray-400 mt-2">Verze podmínek: 1.1 – účinná od 1. 5. 2026</p>
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="agreedToTerms"
                  checked={form.agreedToTerms}
                  onChange={handleChange}
                  className="mt-0.5 w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                />
                <span className="text-sm text-gray-700">
                  Přečetl/a jsem si a souhlasím s úplnými podmínkami partnerského programu, včetně zpracování osobních údajů dle GDPR, výpočtu odměn, mlčenlivosti a výpovědních podmínek. Potvrzuji, že jsem oprávněn/a jednat jménem výše uvedeného subjektu.
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-semibold py-3 rounded-xl transition-colors text-sm"
            >
              {loading ? "Registruji..." : "Zaregistrovat se"}
            </button>

            <p className="text-center text-sm text-gray-500">
              Již máte účet?{" "}
              <button type="button" onClick={() => navigate("/leadtool")} className="text-red-600 hover:text-red-700 font-medium">
                Přihlaste se
              </button>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
